import React, { useEffect, useMemo, useState } from "react"
import { Button, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from "reactstrap"
import { getAllPlans } from "helpers/backendHelpers/plan"
import { getAddonCatalog } from "helpers/backendHelpers/manageSubscription"
import { generateRenewalPaymentQr, sendRenewalPaymentQr } from "helpers/backendHelpers/renewalReminders"
import LOGO_URL from "../assets/images/logo.png"

const formatAmount = value => `₹${Number(value || 0).toFixed(2)}`
const FINAL_IMAGE_WIDTH = 450
const FINAL_IMAGE_HEIGHT = 720
const QR_SIZE = 250
const durationLabel = plan => `${plan?.noOfBillingCycle || 1} ${plan?.billingCycle || ""}`.trim()

// Kept pixel-for-pixel aligned with Sales Panel's createShareableImage layout.
const createShareableImage = (qrCodeUrl, finalAmount, calculationData, amountLabel = "Original Plan Amount:") => new Promise((resolve, reject) => {
  const { originalAmount, balanceAdjustment, planAmount, discountAmount, taxableAmount, taxAmount, finalAmount: totalAmount } = calculationData
  const hasBalanceAdjustment = Number(balanceAdjustment || 0) > 0
  // Balance Adjustment adds two rows, so Discount and every element below it
  // must move by two line heights to prevent text overlapping in the image.
  const layoutOffset = hasBalanceAdjustment ? 60 : 0
  const canvas = document.createElement("canvas")
  canvas.width = FINAL_IMAGE_WIDTH
  canvas.height = FINAL_IMAGE_HEIGHT + layoutOffset + 20
  const ctx = canvas.getContext("2d")
  const formatter = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 })
  const logo = new Image()
  const qr = new Image()

  const drawImage = () => {
    ctx.fillStyle = "#ffffff"
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    const drawLine = (y, label, value, color = "#1f2937") => {
      ctx.fillStyle = color
      ctx.font = "18px Arial"
      ctx.textAlign = "left"
      ctx.fillText(label, 30, y)
      ctx.textAlign = "right"
      ctx.fillText(value, FINAL_IMAGE_WIDTH - 30, y)
    }
    const headerHeight = 80
    ctx.drawImage(logo, 30, 25, 40, 40)
    ctx.fillStyle = "#115ada"
    ctx.font = "bold 30px Inter, Arial"
    ctx.textAlign = "left"
    ctx.fillText("Bill Saathi", 85, 55)
    ctx.strokeStyle = "#6366f1"
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.moveTo(0, headerHeight)
    ctx.lineTo(FINAL_IMAGE_WIDTH, headerHeight)
    ctx.stroke()

    const lineYStart = headerHeight + 40
    const lineSpacing = 30
    drawLine(lineYStart, amountLabel, formatter.format(originalAmount))
    if (hasBalanceAdjustment) {
      drawLine(lineYStart + lineSpacing, "Balance Adjustment:", `- ${formatter.format(balanceAdjustment)}`, "#dc2626")
      drawLine(lineYStart + lineSpacing * 2, "Plan Amount:", formatter.format(planAmount))
    }
    drawLine(lineYStart + lineSpacing + layoutOffset, "Discount:", `- ${formatter.format(discountAmount)}`, "#dc2626")
    drawLine(lineYStart + lineSpacing * 2 + layoutOffset, "Amount After Discount:", formatter.format(taxableAmount))
    drawLine(lineYStart + lineSpacing * 3 + layoutOffset, "GST (18%):", `+ ${formatter.format(taxAmount)}`)
    const totalY = lineYStart + lineSpacing * 4 + 20 + layoutOffset
    ctx.fillStyle = "#1f2937"
    ctx.font = "bold 22px Arial"
    ctx.textAlign = "left"
    ctx.fillText("TOTAL DUE:", 30, totalY)
    ctx.textAlign = "right"
    ctx.fillStyle = "#10b981"
    ctx.fillText(formatter.format(totalAmount), FINAL_IMAGE_WIDTH - 30, totalY)
    ctx.strokeStyle = "#a5b4fc"
    ctx.beginPath()
    ctx.moveTo(30, totalY + 20)
    ctx.lineTo(FINAL_IMAGE_WIDTH - 30, totalY + 20)
    ctx.stroke()

    const qrX = (FINAL_IMAGE_WIDTH - QR_SIZE) / 2
    const qrY = totalY + 50
    ctx.drawImage(qr, qrX, qrY, QR_SIZE, QR_SIZE)
    const centerSize = 60
    const centerOffset = (QR_SIZE - centerSize) / 2
    ctx.fillStyle = "#ffffff"
    ctx.fillRect(qrX + centerOffset - 2, qrY + centerOffset - 2, centerSize + 4, centerSize + 4)
    ctx.drawImage(logo, qrX + centerOffset, qrY + centerOffset, centerSize, centerSize)

    let footerY = qrY + QR_SIZE + 40
    ctx.fillStyle = "#1f2937"
    ctx.font = "bold 20px Arial"
    ctx.textAlign = "center"
    ctx.fillText("SCAN TO PAY", FINAL_IMAGE_WIDTH / 2, footerY)
    footerY += 40
    ctx.fillStyle = "#0f8a00"
    ctx.font = "bold 36px Arial"
    ctx.fillText(formatter.format(finalAmount), FINAL_IMAGE_WIDTH / 2, footerY)
    footerY += 25
    ctx.fillStyle = "#4b5563"
    ctx.font = "16px Arial"
    ctx.fillText("UPI ID: sanketvanani6492-1@okhdfcbank", FINAL_IMAGE_WIDTH / 2, footerY)
    footerY += 35
    ctx.fillStyle = "#6366f1"
    ctx.font = "italic 16px Arial"
    ctx.fillText("Please download this image and share it on WhatsApp.", FINAL_IMAGE_WIDTH / 2, footerY)
    resolve(canvas.toDataURL("image/png"))
  }

  let loaded = 0
  const onLoad = () => {
    loaded += 1
    if (loaded === 2) drawImage()
  }
  logo.onload = onLoad
  qr.onload = onLoad
  logo.onerror = qr.onerror = () => reject(new Error("Payment QR image could not be created"))
  logo.src = LOGO_URL
  qr.src = qrCodeUrl
})

const GST_PERCENTAGE = 18
const MULTI_YEAR_ADDON_CODES = ["whatsappService", "jobManagement"]
const estimateCalculation = amount => {
  const base = Number(amount || 0)
  const taxAmount = Number((base * GST_PERCENTAGE / 100).toFixed(2))
  return {
    originalAmount: base, balanceAdjustment: 0, planAmount: base, discountAmount: 0, taxableAmount: base,
    taxPercentage: GST_PERCENTAGE, taxAmount, finalAmount: Number((base + taxAmount).toFixed(2)),
  }
}
// Same pricing rules as the Add-on purchase modal on the User page.
const addonUnitPrice = addon => Number((addon?.hasCredits ? addon?.creditConfig?.pricePerUnit : addon?.price) || 0)
const creditUnitPrice = addon => Number(
  addon?.creditConfig?.pricePerCredit
  || (addon?.creditConfig?.pricePerUnit / addon?.creditConfig?.creditsPerUnit)
  || 0
)

const CalculationSummary = ({ calculation, amountLabel = "Original Plan Amount" }) => (
  <div className="border rounded p-3 bg-light">
    <div className="d-flex justify-content-between mb-2"><span>{amountLabel}</span><strong>{formatAmount(calculation.originalAmount)}</strong></div>
    {Number(calculation.balanceAdjustment || 0) > 0 && <>
      <div className="d-flex justify-content-between mb-2 text-danger"><span>Balance Adjustment</span><strong>- {formatAmount(calculation.balanceAdjustment)}</strong></div>
      <div className="d-flex justify-content-between mb-2"><span>Plan Amount</span><strong>{formatAmount(calculation.planAmount)}</strong></div>
    </>}
    <div className="d-flex justify-content-between mb-2 text-danger"><span>Discount Applied</span><strong>- {formatAmount(calculation.discountAmount)}</strong></div>
    <div className="d-flex justify-content-between mb-2"><span>Amount After Discount</span><strong>{formatAmount(calculation.taxableAmount)}</strong></div>
    <div className="d-flex justify-content-between mb-3"><span>GST ({calculation.taxPercentage}%)</span><strong>+ {formatAmount(calculation.taxAmount)}</strong></div>
    <div className="border-top pt-3 d-flex justify-content-between fs-5"><strong>Total Due</strong><strong className="text-success">{formatAmount(calculation.finalAmount)}</strong></div>
  </div>
)

const DiscountToggle = ({ applied, label, onToggle }) => (
  <Button
    type="button"
    color={applied ? "primary" : "light"}
    outline={!applied}
    className="d-inline-flex align-items-center justify-content-center px-3 py-2"
    style={{ minWidth: 220, fontWeight: 500, borderColor: applied ? undefined : "#cbd5e1" }}
    onClick={onToggle}
  >
    <i className={`bx ${applied ? "bx-checkbox-checked" : "bx-checkbox"} me-2`} style={{ fontSize: 20 }} />
    {label}
  </Button>
)

const QrPreview = ({ src, alt }) => (
  <Col md={5} className="text-center mt-3 mt-md-0">
    <img src={src} alt={alt} className="border shadow-sm" style={{ width: 150, maxWidth: "100%" }} />
  </Col>
)

const RenewalPaymentQrModal = ({ isOpen, toggle, row, startDate, endDate, onSent }) => {
  const [planGroups, setPlanGroups] = useState([])
  const [selectedGroupName, setSelectedGroupName] = useState("")
  const [selectedPlanId, setSelectedPlanId] = useState("")
  const [isDiscountApplied, setIsDiscountApplied] = useState(false)
  const [couponCode, setCouponCode] = useState("")
  const [paymentQr, setPaymentQr] = useState(null)
  const [shareableImage, setShareableImage] = useState("")
  const [addonImage, setAddonImage] = useState("")
  const [addonCatalog, setAddonCatalog] = useState([])
  const [addonItems, setAddonItems] = useState([])
  const [creditItems, setCreditItems] = useState([])
  const [selectedAddonId, setSelectedAddonId] = useState("")
  const [selectedYears, setSelectedYears] = useState(1)
  const [selectedCreditAddonId, setSelectedCreditAddonId] = useState("")
  const [selectedCredits, setSelectedCredits] = useState("")
  const [isAddonDiscountApplied, setIsAddonDiscountApplied] = useState(false)
  const [addonCouponCode, setAddonCouponCode] = useState("")
  const [loadingPlans, setLoadingPlans] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [sending, setSending] = useState(false)

  useEffect(() => {
    if (!isOpen || !row) return
    let active = true
    const loadPlans = async () => {
      setLoadingPlans(true)
      setPaymentQr(null)
      setShareableImage("")
      setAddonImage("")
      setAddonItems([])
      setCreditItems([])
      setSelectedAddonId("")
      setSelectedYears(1)
      setSelectedCreditAddonId("")
      setSelectedCredits("")
      setIsAddonDiscountApplied(false)
      setAddonCouponCode("")
      setIsDiscountApplied(false)
      setCouponCode("")
      try {
        const [response, addonResponse] = await Promise.all([
          getAllPlans(),
          getAddonCatalog().catch(() => null),
        ])
        const groups = response?.payload?.data || []
        if (!active) return
        setPlanGroups(groups)
        // Every active catalog add-on is selectable, regardless of whether an
        // earlier add-on of the customer has expired.
        setAddonCatalog((addonResponse?.payload || []).filter(addon => addon.isActive))
        const currentGroup = groups.find(group => (group.planCategories || []).some(plan => String(plan._id) === String(row.currentPlanId))) || groups[0]
        const currentPlan = (currentGroup?.planCategories || []).find(plan => String(plan._id) === String(row.currentPlanId)) || currentGroup?.planCategories?.[0]
        setSelectedGroupName(currentGroup?.name || "")
        setSelectedPlanId(currentPlan?._id || "")
      } catch (_) {
        if (active) setPlanGroups([])
      } finally {
        if (active) setLoadingPlans(false)
      }
    }
    loadPlans()
    return () => { active = false }
  }, [isOpen, row])

  const selectedGroup = useMemo(
    () => planGroups.find(group => group.name === selectedGroupName) || null,
    [planGroups, selectedGroupName]
  )
  const selectedPlan = useMemo(
    () => selectedGroup?.planCategories?.find(plan => String(plan._id) === String(selectedPlanId)) || null,
    [selectedGroup, selectedPlanId]
  )
  const calculation = paymentQr?.calculation || estimateCalculation(selectedPlan?.planAmount)

  const findAddon = id => addonCatalog.find(addon => String(addon._id) === String(id)) || null
  const selectedAddon = findAddon(selectedAddonId)
  const isMultiYear = MULTI_YEAR_ADDON_CODES.includes(selectedAddon?.code)
  const years = isMultiYear ? Math.max(0, Number(selectedYears) || 0) : 1
  const selectedDurationDays = selectedAddon && years ? (selectedAddon.defaultDurationDays || 365) * years : ""
  const selectedPrice = selectedAddon && years ? Number((addonUnitPrice(selectedAddon) * years).toFixed(2)) : ""
  const selectedCreditAddon = findAddon(selectedCreditAddonId)
  const creditQty = Math.floor(Number(selectedCredits) || 0)
  const selectedCreditPrice = selectedCreditAddon && creditQty > 0 ? Number((creditUnitPrice(selectedCreditAddon) * creditQty).toFixed(2)) : ""

  const hasAddons = addonItems.length > 0 || creditItems.length > 0
  const addonSubtotal = [...addonItems, ...creditItems].reduce((sum, item) => sum + item.price, 0)
  const addonCalculation = paymentQr?.addon?.calculation || estimateCalculation(addonSubtotal)

  const resetPaymentQr = () => {
    setPaymentQr(null)
    setShareableImage("")
    setAddonImage("")
  }

  const handleGroupChange = event => {
    const nextGroup = planGroups.find(group => group.name === event.target.value)
    setSelectedGroupName(event.target.value)
    setSelectedPlanId(nextGroup?.planCategories?.[0]?._id || "")
    resetPaymentQr()
  }

  const addAddonItem = () => {
    if (!selectedAddon || selectedPrice === "") return
    if (addonItems.some(item => item.addonId === selectedAddon._id)) return
    setAddonItems(items => [...items, {
      addonId: selectedAddon._id,
      durationDays: selectedDurationDays,
      price: selectedPrice,
      ...(isMultiYear ? { years } : {}),
    }])
    setSelectedAddonId("")
    setSelectedYears(1)
    resetPaymentQr()
  }

  const addCreditItem = () => {
    if (!selectedCreditAddon || selectedCreditPrice === "") return
    if (creditItems.some(item => item.addonId === selectedCreditAddon._id)) return
    setCreditItems(items => [...items, { addonId: selectedCreditAddon._id, credits: creditQty, price: selectedCreditPrice }])
    setSelectedCreditAddonId("")
    setSelectedCredits("")
    resetPaymentQr()
  }

  const removeAddonItem = index => { setAddonItems(items => items.filter((_, idx) => idx !== index)); resetPaymentQr() }
  const removeCreditItem = index => { setCreditItems(items => items.filter((_, idx) => idx !== index)); resetPaymentQr() }

  // Base plan and add-on discounts are independent: each has its own coupon.
  const buildPayload = () => ({
    userId: row.userId,
    startDate,
    endDate,
    planId: selectedPlanId,
    couponCode: isDiscountApplied ? couponCode.trim() : "",
    addons: addonItems.map(({ addonId, years: itemYears }) => ({ addonId, ...(itemYears ? { years: itemYears } : {}) })),
    creditPurchases: creditItems.map(({ addonId, credits }) => ({ addonId, credits })),
    addonCouponCode: isAddonDiscountApplied && hasAddons ? addonCouponCode.trim() : "",
  })

  const generateQr = async () => {
    if (!row?.userId || !selectedPlanId) return
    setGenerating(true)
    try {
      const response = await generateRenewalPaymentQr(buildPayload())
      const generatedQr = response?.payload
      const image = await createShareableImage(
        generatedQr?.rawQrBase64,
        generatedQr?.calculation?.finalAmount,
        generatedQr?.calculation || {}
      )
      let generatedAddonImage = ""
      if (generatedQr?.addon) {
        generatedAddonImage = await createShareableImage(
          generatedQr.addon.rawQrBase64,
          generatedQr.addon.calculation?.finalAmount,
          generatedQr.addon.calculation || {},
          "Add-on Amount:"
        )
      }
      setShareableImage(image)
      setAddonImage(generatedAddonImage)
      setPaymentQr({ ...generatedQr, qrBase64: image })
    } catch (_) {
      setPaymentQr(null)
    } finally {
      setGenerating(false)
    }
  }

  const sendQr = async () => {
    if (!row?.userId || !selectedPlanId || !paymentQr) return
    setSending(true)
    try {
      await sendRenewalPaymentQr({ ...buildPayload(), shareableImage, addonShareableImage: addonImage })
      await onSent?.()
      toggle()
    } catch (_) {
      // Persisted QR failures (for example, not_on_whatsapp) are displayed in
      // the list. Close this modal so its retry action is immediately visible.
      await onSent?.()
      toggle()
    } finally {
      setSending(false)
    }
  }

  const downloadImage = (href, fileName) => {
    const link = document.createElement("a")
    link.href = href
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const downloadQr = () => {
    if (!shareableImage) return
    const safeName = String(row?.customerName || "customer").replace(/[^a-z0-9_-]/gi, "_")
    downloadImage(shareableImage, `BillSaathi_Renewal_QR_${safeName}.png`)
    if (addonImage) downloadImage(addonImage, `BillSaathi_Addon_QR_${safeName}.png`)
  }

  const missingCoupon = (isDiscountApplied && !couponCode.trim()) || (isAddonDiscountApplied && hasAddons && !addonCouponCode.trim())

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>Generate Renewal Payment QR Code</ModalHeader>
      <ModalBody>
        <p className="mb-1">Generate a payment QR code for <strong>{row?.customerName}</strong>.</p>
        <p className="text-muted small mb-4">Current plan: {row?.planName} ({row?.duration})</p>

        {loadingPlans ? <div className="text-center py-5"><Spinner color="primary" /></div> : (
          <>
            <Row>
              <Col md={6} className="mb-3">
                <Label>Plan</Label>
                <Input type="select" value={selectedGroupName} onChange={handleGroupChange}>
                  {planGroups.map(group => <option key={group.name} value={group.name}>{group.name}</option>)}
                </Input>
              </Col>
              <Col md={6} className="mb-3">
                <Label>Duration</Label>
                <Input type="select" value={selectedPlanId} onChange={event => { setSelectedPlanId(event.target.value); resetPaymentQr() }}>
                  {(selectedGroup?.planCategories || []).map(plan => (
                    <option key={plan._id} value={plan._id}>{formatAmount(plan.planAmount)} - {durationLabel(plan)}</option>
                  ))}
                </Input>
              </Col>
            </Row>

            <div className="mb-3">
              <DiscountToggle
                applied={isDiscountApplied}
                label="Apply discount coupon"
                onToggle={() => { setIsDiscountApplied(current => !current); setCouponCode(""); resetPaymentQr() }}
              />
            </div>
            {isDiscountApplied && (
              <div className="mb-3">
                <Label>Coupon Code</Label>
                <Input value={couponCode} maxLength="100" placeholder="Enter coupon code" onChange={event => { setCouponCode(event.target.value.toUpperCase()); resetPaymentQr() }} />
              </div>
            )}

            <Row className="align-items-center mt-3">
              <Col md={paymentQr ? 7 : 12}>
                {hasAddons && <h6 className="mb-2">Base Plan</h6>}
                <CalculationSummary calculation={calculation} />
              </Col>
              {paymentQr && <QrPreview src={paymentQr.qrBase64} alt="Bill Saathi renewal payment QR code" />}
            </Row>

            <hr className="my-4" />
            <h6 className="mb-3">Add-ons (optional)</h6>

            <Row className="align-items-end">
              <Col md={isMultiYear ? 3 : 4} className="mb-3">
                <Label>Add-on</Label>
                <Input type="select" value={selectedAddonId} onChange={event => { setSelectedAddonId(event.target.value); setSelectedYears(1) }}>
                  <option value="">Select Add-on</option>
                  {addonCatalog.map(addon => <option key={addon._id} value={addon._id}>{addon.name}</option>)}
                </Input>
              </Col>
              {isMultiYear && (
                <Col md={2} className="mb-3">
                  <Label>Years</Label>
                  <Input type="number" min="1" value={selectedYears} onChange={event => setSelectedYears(event.target.value)} />
                </Col>
              )}
              <Col md={isMultiYear ? 3 : 4} className="mb-3">
                <Label>Duration (Days)</Label>
                <Input type="number" value={selectedDurationDays} disabled />
              </Col>
              <Col md={3} className="mb-3">
                <Label>Price</Label>
                <Input type="number" value={selectedPrice} disabled />
              </Col>
              <Col md={1} className="mb-3">
                <Button color="primary" type="button" onClick={addAddonItem}>+</Button>
              </Col>
            </Row>
            {addonItems.map((item, idx) => (
              <div key={item.addonId} className="d-flex justify-content-between align-items-center border rounded p-2 mb-2">
                <div>{findAddon(item.addonId)?.name || "Add-on"} | {item.durationDays} days | Rs. {item.price}</div>
                <Button color="danger" size="sm" onClick={() => removeAddonItem(idx)}>Remove</Button>
              </div>
            ))}

            <Row className="align-items-end">
              <Col md={4} className="mb-3">
                <Label>Credit Add-on</Label>
                <Input type="select" value={selectedCreditAddonId} onChange={event => setSelectedCreditAddonId(event.target.value)}>
                  <option value="">Select Add-on</option>
                  {addonCatalog.filter(addon => addon.hasCredits).map(addon => <option key={addon._id} value={addon._id}>{addon.name}</option>)}
                </Input>
              </Col>
              <Col md={3} className="mb-3">
                <Label>Credits</Label>
                <Input type="number" min="1" value={selectedCredits} onChange={event => setSelectedCredits(event.target.value)} />
              </Col>
              <Col md={4} className="mb-3">
                <Label>Price</Label>
                <Input type="number" value={selectedCreditPrice} disabled />
              </Col>
              <Col md={1} className="mb-3">
                <Button color="primary" type="button" onClick={addCreditItem}>+</Button>
              </Col>
            </Row>
            {creditItems.map((item, idx) => (
              <div key={item.addonId} className="d-flex justify-content-between align-items-center border rounded p-2 mb-2">
                <div>{findAddon(item.addonId)?.name || "Add-on"} | {item.credits} credits | Rs. {item.price}</div>
                <Button color="danger" size="sm" onClick={() => removeCreditItem(idx)}>Remove</Button>
              </div>
            ))}

            {hasAddons && (
              <>
                <div className="mb-3 mt-2">
                  <DiscountToggle
                    applied={isAddonDiscountApplied}
                    label="Apply add-on discount coupon"
                    onToggle={() => { setIsAddonDiscountApplied(current => !current); setAddonCouponCode(""); resetPaymentQr() }}
                  />
                </div>
                {isAddonDiscountApplied && (
                  <div className="mb-3">
                    <Label>Add-on Coupon Code</Label>
                    <Input value={addonCouponCode} maxLength="100" placeholder="Enter add-on coupon code" onChange={event => { setAddonCouponCode(event.target.value.toUpperCase()); resetPaymentQr() }} />
                  </div>
                )}
                <Row className="align-items-center mt-3">
                  <Col md={paymentQr ? 7 : 12}>
                    <h6 className="mb-2">Add-ons Total</h6>
                    <CalculationSummary calculation={addonCalculation} amountLabel="Add-on Amount" />
                  </Col>
                  {paymentQr && addonImage && <QrPreview src={addonImage} alt="Bill Saathi add-on payment QR code" />}
                </Row>
              </>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={toggle}>Close</Button>
        {paymentQr ? (
          <>
            <Button color="primary" outline onClick={downloadQr}>
              <i className="bx bx-download me-1" />{addonImage ? "Download QRs" : "Download QR"}
            </Button>
            <Button color="success" disabled={sending || !row?.mobileNo} onClick={sendQr}>
              {sending ? <><Spinner size="sm" className="me-1" />Sending...</> : <><i className="bx bxl-whatsapp me-1" />{addonImage ? "Send Both QRs to User" : "Send QR to User"}</>}
            </Button>
          </>
        ) : (
          <Button color="primary" disabled={loadingPlans || generating || !selectedPlanId || missingCoupon} onClick={generateQr}>
            {generating ? <><Spinner size="sm" className="me-1" />Generating...</> : "Generate QR Code"}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  )
}

export default RenewalPaymentQrModal
